// src/services/offlineStorage.js
// ─────────────────────────────────────────────────────────────────────────────
//  نظام التخزين المشفر — IndexedDB + AES-GCM
//  يدعم: الجلسة، الكتب، الـ PDFs، الطلبات، الرسائل، المحادثات
// ─────────────────────────────────────────────────────────────────────────────

import { getBackblazeDownloadUrl } from '../config/backblaze';

const DB_NAME    = 'khidma_offline_db';
const DB_VERSION = 2; // ⬆️ رفعنا الإصدار لإضافة stores جديدة
const STORES = {
  BOOKS:         'books',
  PDFS:          'pdfs',
  SESSION:       'session',
  KEYS:          'keys',
  REQUESTS:      'requests',       // 🆕 الطلبات
  CONVERSATIONS: 'conversations',  // 🆕 المحادثات
  MESSAGES:      'messages',       // 🆕 الرسائل
  PROFILE:       'profile',        // 🆕 بيانات المستخدم
};

// ─────────────────────────────────────────────────────
//  فتح قاعدة البيانات
// ─────────────────────────────────────────────────────
function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      Object.values(STORES).forEach((store) => {
        if (!db.objectStoreNames.contains(store)) {
          db.createObjectStore(store, { keyPath: 'id' });
        }
      });
    };

    req.onsuccess  = (e) => resolve(e.target.result);
    req.onerror    = (e) => reject(e.target.error);
  });
}

// ─────────────────────────────────────────────────────
//  مساعدات IndexedDB
// ─────────────────────────────────────────────────────
async function dbGet(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function dbPut(storeName, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).put(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function dbGetAll(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

async function dbDelete(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(storeName, 'readwrite');
    const req = tx.objectStore(storeName).delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

// ─────────────────────────────────────────────────────
//  إدارة مفاتيح التشفير (AES-GCM 256-bit)
// ─────────────────────────────────────────────────────
async function getOrCreateEncryptionKey(userId) {
  // ✅ إذا لم يكن السياق آمناً، نرجع null لاستخدام وضع الـ Fallback
  if (!isSecureContext || !window.crypto?.subtle) {
    console.warn('⚠️ [Offline] Non-secure context (HTTP). Using Base64 fallback mode.');
    return null;
  }

  const stored = await dbGet(STORES.KEYS, `key_${userId}`);
  if (stored?.key) {
    return await crypto.subtle.importKey(
      'raw',
      base64ToBuffer(stored.key),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  const exported = await crypto.subtle.exportKey('raw', key);
  await dbPut(STORES.KEYS, {
    id: `key_${userId}`,
    key: bufferToBase64(exported),
    created_at: Date.now(),
  });

  return key;
}
const isSecureContext = window.isSecureContext || 
                        window.location.protocol === 'https:' || 
                        window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';

// ─────────────────────────────────────────────────────
//  تشفير وفك تشفير
// ─────────────────────────────────────────────────────
async function encrypt(data, key) {
  const encoded = typeof data === 'string'
    ? new TextEncoder().encode(data)
    : new Uint8Array(data);

  // ✅ وضع Fallback: تشفير Base64 بسيط إذا لم يكن هناك مفتاح
  if (!key) {
     // نستخدم btoa مع معالجة Unicode
     const str = String.fromCharCode.apply(null, encoded);
     return btoa(str);
  }

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);

  const result = new Uint8Array(iv.length + encrypted.byteLength);
  result.set(iv, 0);
  result.set(new Uint8Array(encrypted), iv.length);

  return bufferToBase64(result.buffer);
}

async function decrypt(encryptedBase64, key, returnType = 'string') {
  // ✅ وضع Fallback: فك تشفير Base64
  if (!key) {
     const binaryStr = atob(encryptedBase64);
     const bytes = new Uint8Array(binaryStr.length);
     for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
     
     if (returnType === 'arraybuffer') return bytes.buffer;
     return new TextDecoder().decode(bytes);
  }

  const buffer = base64ToBuffer(encryptedBase64);
  const iv = buffer.slice(0, 12);
  const data = buffer.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(iv) },
    key,
    data
  );

  if (returnType === 'arraybuffer') return decrypted;
  return new TextDecoder().decode(decrypted);
}

// ─────────────────────────────────────────────────────
//  مساعدات base64
// ─────────────────────────────────────────────────────
function bufferToBase64(buffer) {
  const bytes  = new Uint8Array(buffer);
  let   binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

// ─────────────────────────────────────────────────────
//  API الرئيسي
// ─────────────────────────────────────────────────────
const offlineStorage = {

  // ══════════════════════════════════════════
  //  الجلسة
  // ══════════════════════════════════════════
  async saveSession(userId, sessionData) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(sessionData), key);
      await dbPut(STORES.SESSION, {
        id:       'current_session',
        user_id:  userId,
        data:     encrypted,
        saved_at: Date.now(),
      });
      console.log('✅ [Offline] Session saved & encrypted');
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save session:', err);
      return false;
    }
  },

  async loadSession() {
    try {
      const record = await dbGet(STORES.SESSION, 'current_session');
      if (!record) return null;

      const key     = await getOrCreateEncryptionKey(record.user_id);
      const json    = await decrypt(record.data, key);
      const session = JSON.parse(json);
      console.log('✅ [Offline] Session loaded from cache');
      return { session, userId: record.user_id };
    } catch (err) {
      console.error('❌ [Offline] Failed to load session:', err);
      return null;
    }
  },

  async clearSession() {
    try {
      await dbDelete(STORES.SESSION, 'current_session');
      console.log('✅ [Offline] Session cleared');
    } catch (err) {
      console.error('❌ [Offline] Failed to clear session:', err);
    }
  },

  // ══════════════════════════════════════════
  //  🆕 الطلبات (Requests)
  // ══════════════════════════════════════════
  async saveRequests(userId, requests) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(requests), key);
      await dbPut(STORES.REQUESTS, {
        id:       `requests_${userId}`,
        user_id:  userId,
        data:     encrypted,
        saved_at: Date.now(),
      });
      console.log(`✅ [Offline] Requests saved (${requests.length} items)`);
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save requests:', err);
      return false;
    }
  },

  async loadRequests(userId) {
    try {
      const record = await dbGet(STORES.REQUESTS, `requests_${userId}`);
      if (!record) return null;

      const key  = await getOrCreateEncryptionKey(record.user_id);
      const json = await decrypt(record.data, key);
      const list = JSON.parse(json);
      console.log(`✅ [Offline] Requests loaded (${list.length} items)`);
      return list;
    } catch (err) {
      console.error('❌ [Offline] Failed to load requests:', err);
      return null;
    }
  },

  // ══════════════════════════════════════════
  //  🆕 المحادثات (Conversations)
  // ══════════════════════════════════════════
  async saveConversations(userId, conversations) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(conversations), key);
      await dbPut(STORES.CONVERSATIONS, {
        id:       `conversations_${userId}`,
        user_id:  userId,
        data:     encrypted,
        saved_at: Date.now(),
      });
      console.log(`✅ [Offline] Conversations saved (${conversations.length} items)`);
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save conversations:', err);
      return false;
    }
  },

  async loadConversations(userId) {
    try {
      const record = await dbGet(STORES.CONVERSATIONS, `conversations_${userId}`);
      if (!record) return null;

      const key  = await getOrCreateEncryptionKey(record.user_id);
      const json = await decrypt(record.data, key);
      const list = JSON.parse(json);
      console.log(`✅ [Offline] Conversations loaded (${list.length} items)`);
      return list;
    } catch (err) {
      console.error('❌ [Offline] Failed to load conversations:', err);
      return null;
    }
  },

  // ══════════════════════════════════════════
  //  🆕 الرسائل (Messages) — مخزنة بالمحادثة
  // ══════════════════════════════════════════
  async saveMessages(userId, conversationId, messages) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(messages), key);
      await dbPut(STORES.MESSAGES, {
        id:              `messages_${conversationId}`,
        user_id:         userId,
        conversation_id: conversationId,
        data:            encrypted,
        saved_at:        Date.now(),
      });
      console.log(`✅ [Offline] Messages saved for conversation ${conversationId} (${messages.length} msgs)`);
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save messages:', err);
      return false;
    }
  },

  async loadMessages(userId, conversationId) {
    try {
      const record = await dbGet(STORES.MESSAGES, `messages_${conversationId}`);
      if (!record) return null;

      const key  = await getOrCreateEncryptionKey(record.user_id);
      const json = await decrypt(record.data, key);
      const list = JSON.parse(json);
      console.log(`✅ [Offline] Messages loaded for conversation ${conversationId} (${list.length} msgs)`);
      return list;
    } catch (err) {
      console.error('❌ [Offline] Failed to load messages:', err);
      return null;
    }
  },

  // ══════════════════════════════════════════
  //  🆕 بيانات الملف الشخصي (Profile)
  // ══════════════════════════════════════════
  async saveProfile(userId, profileData) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(profileData), key);
      await dbPut(STORES.PROFILE, {
        id:       `profile_${userId}`,
        user_id:  userId,
        data:     encrypted,
        saved_at: Date.now(),
      });
      console.log('✅ [Offline] Profile saved');
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save profile:', err);
      return false;
    }
  },

  async loadProfile(userId) {
    try {
      const record = await dbGet(STORES.PROFILE, `profile_${userId}`);
      if (!record) return null;

      const key  = await getOrCreateEncryptionKey(record.user_id);
      const json = await decrypt(record.data, key);
      console.log('✅ [Offline] Profile loaded from cache');
      return JSON.parse(json);
    } catch (err) {
      console.error('❌ [Offline] Failed to load profile:', err);
      return null;
    }
  },

  // ══════════════════════════════════════════
  //  الكتب والـ PDFs (من النسخة القديمة)
  // ══════════════════════════════════════════
  async saveLibrary(userId, libraryList) {
    try {
      const key       = await getOrCreateEncryptionKey(userId);
      const encrypted = await encrypt(JSON.stringify(libraryList), key);
      await dbPut(STORES.BOOKS, {
        id:       `library_${userId}`,
        user_id:  userId,
        data:     encrypted,
        saved_at: Date.now(),
      });
      console.log(`✅ [Offline] Library saved (${libraryList.length} items)`);
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to save library:', err);
      return false;
    }
  },

  async loadLibrary(userId) {
    try {
      const record = await dbGet(STORES.BOOKS, `library_${userId}`);
      if (!record) return null;

      const key  = await getOrCreateEncryptionKey(record.user_id);
      const json = await decrypt(record.data, key);
      const list = JSON.parse(json);
      console.log(`✅ [Offline] Library loaded (${list.length} items)`);
      return list;
    } catch (err) {
      console.error('❌ [Offline] Failed to load library:', err);
      return null;
    }
  },

  async cachePDF(userId, bookId, pdfUrl) {
    try {
      const existing = await dbGet(STORES.PDFS, `pdf_${bookId}`);
      if (existing) return true;

      const response = await fetch(getBackblazeDownloadUrl(pdfUrl) || pdfUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const arrayBuffer = await response.arrayBuffer();
      const key         = await getOrCreateEncryptionKey(userId);
      const encrypted   = await encrypt(arrayBuffer, key);

      await dbPut(STORES.PDFS, {
        id:        `pdf_${bookId}`,
        user_id:   userId,
        book_id:   bookId,
        data:      encrypted,
        size:      arrayBuffer.byteLength,
        cached_at: Date.now(),
      });
      return true;
    } catch (err) {
      console.error(`❌ [Offline] Failed to cache PDF ${bookId}:`, err);
      return false;
    }
  },

  async loadPDF(userId, bookId) {
    try {
      const record = await dbGet(STORES.PDFS, `pdf_${bookId}`);
      if (!record) return null;

      const key         = await getOrCreateEncryptionKey(record.user_id);
      const arrayBuffer = await decrypt(record.data, key, 'arraybuffer');
      const blob        = new Blob([arrayBuffer], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error(`❌ [Offline] Failed to load PDF ${bookId}:`, err);
      return null;
    }
  },

  async isPDFCached(bookId) {
    try {
      const record = await dbGet(STORES.PDFS, `pdf_${bookId}`);
      return !!record;
    } catch { return false; }
  },

  // ══════════════════════════════════════════
  //  حذف كل بيانات المستخدم
  // ══════════════════════════════════════════
  async clearAllUserData(userId) {
    try {
      await dbDelete(STORES.SESSION,       'current_session');
      await dbDelete(STORES.BOOKS,         `library_${userId}`);
      await dbDelete(STORES.KEYS,          `key_${userId}`);
      await dbDelete(STORES.REQUESTS,      `requests_${userId}`);
      await dbDelete(STORES.CONVERSATIONS, `conversations_${userId}`);
      await dbDelete(STORES.PROFILE,       `profile_${userId}`);

      // حذف كل رسائل المحادثات
      const allMessages = await dbGetAll(STORES.MESSAGES);
      const userMessages = allMessages.filter((r) => r.user_id === userId);
      await Promise.all(userMessages.map((r) => dbDelete(STORES.MESSAGES, r.id)));

      // حذف كل PDFs
      const allPdfs = await dbGetAll(STORES.PDFS);
      const userPdfs = allPdfs.filter((r) => r.user_id === userId);
      await Promise.all(userPdfs.map((r) => dbDelete(STORES.PDFS, r.id)));

      console.log('✅ [Offline] All user data cleared');
      return true;
    } catch (err) {
      console.error('❌ [Offline] Failed to clear user data:', err);
      return false;
    }
  },

  // ══════════════════════════════════════════
  //  معلومات التخزين
  // ══════════════════════════════════════════
  async getStorageInfo() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const { usage, quota } = await navigator.storage.estimate();
        return {
          usedMB:  (usage  / 1024 / 1024).toFixed(1),
          totalMB: (quota  / 1024 / 1024).toFixed(0),
          percent: ((usage / quota) * 100).toFixed(1),
        };
      }
      return null;
    } catch { return null; }
  },
};

export default offlineStorage;